import { ReactNode } from 'react';
import { Box, makeStyles, styled } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { SText } from '@setvi/shared/components';

export interface MetricCard {
  title: string;
  value: string | number;
  icon?: ReactNode;
  subtitle?: string;
  /** When set, overrides the default value text color (e.g. emphasis for at-risk $) */
  valueFontColor?: string;
}

interface MetricsGridProps {
  cards: MetricCard[];
  loading?: boolean;
  columns?: number;
}

const useStyles = makeStyles(() => ({
  container: {
    display: 'grid',
    gridTemplateColumns: ({ columns }: { columns: number }) =>
      `repeat(${columns}, 1fr)`,
    gridGap: 12,
    width: '100%',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: 'repeat(2, 1fr)'
    },
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr'
    }
  },
  card: {
    height: 100,
    borderRadius: 20,
    padding: '12px 16px',
    border: '1px solid #EEF0F2',
    background: '#FFFFFF',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  cardFooter: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0px 0px 0px 6px',
    justifyContent: 'space-between',
    alignItems: 'flex-end'
  },
  skeleton: {
    borderRadius: 8
  }
}));

const IconContainer = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#F8F9FA',
  border: '1px solid #EEF0F2'
});

const MetricCardComponent = ({
  title,
  value,
  icon,
  subtitle,
  valueFontColor,
  loading
}: MetricCard & { loading?: boolean }) => {
  const classes = useStyles({ columns: 4 });

  return (
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        {icon && <IconContainer>{icon}</IconContainer>}
        <SText size="md" weight="semibold" fontColor="#5D7083">
          {title || '-'}
        </SText>
      </Box>

      <Box className={classes.cardFooter}>
        {loading ? (
          <Skeleton
            width={70}
            height={22}
            variant="rect"
            className={classes.skeleton}
          />
        ) : (
          <SText
            size="xl"
            weight="semibold"
            fontColor={valueFontColor ? undefined : '#333D47'}
            style={valueFontColor ? { color: valueFontColor } : undefined}>
            {value || '-'}
          </SText>
        )}

        {subtitle && (
          <SText size="sm" fontColor="#5D7083">
            {subtitle}
          </SText>
        )}
      </Box>
    </Box>
  );
};

/**
 * MetricsGrid Component
 * Reusable grid component for displaying metric cards
 *
 * @param cards - Array of metric card objects with title, value, icon, and optional subtitle
 * @param loading - Show loading skeleton state
 * @param columns - Number of columns in the grid (default: 4)
 */
export const MetricsGrid = ({
  cards,
  loading = false,
  columns = 4
}: MetricsGridProps) => {
  const classes = useStyles({ columns });

  return (
    <Box className={classes.container}>
      {cards.map(card => (
        <MetricCardComponent key={card.title} {...card} loading={loading} />
      ))}
    </Box>
  );
};
